import { typedAction } from "../helpers";
import { SharepointInfoState } from "../models/reduxModels";
import { RESET_SHAREPOINT_INFO, SET_SHAREPOINT_INFO, SET_IS_SITES_LOADING, SET_SITES_LAST_CACHE_REFRESH } from "./actionTypes";
import authModule from "../../../../utilities/AuthModule";
import { getAllSubSites, searchSharePointSites } from "../../services/GraphService";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { Action } from "redux";
import { decrementAmountOfLoaders, incrementAmountOfLoaders } from "./loader";
import { logger } from "../../../../utilities/logger";
import { IHubSite } from "../../interfaces/IHubSite";
import { getHubSites, getSharepointListItems } from "../../services/SharePointRestServices";
import { getInactiveClientSites } from "../../services/inactiveClientSites";
import { SharePointSiteHelper } from "../../../../utilities/SharePointSiteHelper";
import { ISiteInfo, ISiteListLoad, ISiteSearchRequest, ISiteSearchResult, filterNonTopLevelISiteInfoArray } from "../../interfaces/ISiteInfo";
import { IGraphSearchPager } from "../../interfaces/IGraphSearchPager";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../../components/helpers/localStorageHelper";
import { IManagedSite, IManagedSiteCollection } from "../../interfaces/IManagedSite";
import { docubirdManagedSitesDatasource } from "../../interfaces/ITenantInfo";
import { apiGetProvisionedSites, apiRemoveProvisionedSites } from "../../services/ApiService";
import { dedupeSitesByIdentity } from "./sharepointInfoHelpers";
import { sameAccountId } from "../../../../utilities/sameAccountId";

const initialState: SharepointInfoState = { mainUrl: "", isSitesLoading: false, sites: null, pageData: null };

const setIsSitesLoading = (isSitesLoading: boolean) => {
  return typedAction(SET_IS_SITES_LOADING, isSitesLoading);
}

export const setSharepointInfo = (info: SharepointInfoState) => {
  return typedAction(SET_SHAREPOINT_INFO, info);
};

const setSitesLastCacheRefresh = () => {
  return typedAction(SET_SITES_LAST_CACHE_REFRESH);
}

export const resetSharepointInfo = () => {
  return typedAction(RESET_SHAREPOINT_INFO);
}

export const getSites = (allowedSiteCollections?: string[], previousPageData?: IGraphSearchPager, skipLoaders: boolean = false, useCachedSites: boolean = false): ThunkAction<Promise<ISiteInfo[]>, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  // 2026-10-29 (audit C): the sign-in this load reads for (see the check after getAllSites)
  const resetsAtStart: number = SharePointSiteHelper.resets;
  const signedInAs: string = (await authModule.getAccount(false))?.accountId;
  const allowedSites: string[] = allowedSiteCollections ?? getState().tenantInfo.allowedSiteCollections;

  if (useCachedSites) {
    const cachedSites = getLocalStorage(localStorageKeys.sites);
    if (Array.isArray(cachedSites) && cachedSites?.length > 0) {
      dispatch(
        setSharepointInfo({
          isSitesLoading: false,
          mainUrl: await getBaseSiteUrl(),
          // 2026-10-29 (audit B): an outage keeps the saved copy, so one saved before an admin narrowed the setting can outlive it
          sites: filterAllowedSites(cachedSites, allowedSites, rememberedProvisionedSites(getState().tenantInfo.enableProvisionedSiteRegistration)),
          lastCacheFetch: new Date().getTime()
        })
      );
      dispatch(decrementAmountOfLoaders());
    }
    else {
      dispatch(setSitesLastCacheRefresh());
    }
  }

  let currentState = getState();
  let sites: ISiteInfo[] = currentState.sharePointInfo.sites ?? [];

  if (!currentState.sharePointInfo.isSitesLoading) {

    if (!skipLoaders) {
      if (!previousPageData?.moreResultsAvailable) dispatch(incrementAmountOfLoaders());
      dispatch(setIsSitesLoading(true));
    }

    let siteCollections: ISiteSearchResult = await getAllSites(
      allowedSites,
      previousPageData,
      currentState.tenantInfo.enableProvisionedSiteRegistration,
      currentState.tenantInfo.docubirdManagedSitesDatasources,
      currentState.tenantInfo.clientListUrl
    );

    // 2026-10-29 (audit C): a load that outlived its sign-in (a sign-out, even when the same account signed in again, or
    // another account's sign-in) writes nothing: what it read belongs to the sign-in it started under, and the next one
    // loads its own list (its mount effect loads only when there is none). Before the batch a failed provisioned-sites call
    // ended such a load; with registration off it wrote an empty list that the next sign-in kept.
    if (SharePointSiteHelper.resets !== resetsAtStart || !sameAccountId(signedInAs, (await authModule.getAccount(false))?.accountId)) {
      if (!skipLoaders) {
        dispatch(setIsSitesLoading(false));
        if (!previousPageData?.moreResultsAvailable) dispatch(decrementAmountOfLoaders());
      }
      return getState().sharePointInfo.sites ?? [];
    }

    // 2026-10-27: every list-producing source failed for an outage reason (expired token, network, throttling):
    // keep the cached list, its paging and its cache instead of overwriting them with the empty answer.
    if (siteCollections?.outageFailure) {
      // The load still finishes (consumers wait for lastServerFetch and mainUrl); only the cache write is skipped.
      // pageData and lastCacheFetch stay as they are so a load-more remains retryable. The list on screen is kept rather
      // than the saved one: only this thunk writes it, so where they differ the save failed (a full localStorage), and a
      // sign-out clears the store (CLEAR_ALL_STATE), so it is this account's (2026-10-28 audit).
      const kept = getState().sharePointInfo;
      // 2026-10-29: with the allowed-sites setting applied as on an answered load (an admin may have narrowed it). Not on
      // a load-more: an answered one doesn't filter the list it adds to either, and before the batch a failed one kept it.
      const keptSites: ISiteInfo[] = previousPageData?.moreResultsAvailable
        ? kept.sites ?? []
        : filterAllowedSites(kept.sites ?? [], allowedSites, rememberedProvisionedSites(currentState.tenantInfo.enableProvisionedSiteRegistration));
      dispatch(setSharepointInfo({ ...kept, isSitesLoading: false, sites: keptSites, mainUrl: kept.mainUrl || await getBaseSiteUrl(), lastServerFetch: new Date().getTime() }));

      if (!skipLoaders && !previousPageData?.moreResultsAvailable) dispatch(decrementAmountOfLoaders());

      return keptSites;
    }

    if (previousPageData?.moreResultsAvailable) {
      siteCollections.sites = siteCollections?.sites?.length > 0
        ? (currentState.sharePointInfo.sites?.filter(existingSite => !siteCollections.sites.some(newSite => existingSite.id === newSite.id)) ?? []).concat(siteCollections.sites)
        : currentState.sharePointInfo.sites;

      siteCollections.sites = dedupeSitesByIdentity(siteCollections.sites);
    }

    setLocalStorage(localStorageKeys.sites, siteCollections.sites);

    dispatch(
      setSharepointInfo({
        isSitesLoading: false,
        mainUrl: await getBaseSiteUrl(),
        sites: siteCollections.sites,
        pageData: siteCollections?.pageData,
        lastCacheFetch: currentState.sharePointInfo.lastCacheFetch,
        lastServerFetch: new Date().getTime(),
      })
    );

    if (!skipLoaders && !previousPageData?.moreResultsAvailable) dispatch(decrementAmountOfLoaders());

    sites = siteCollections.sites;
  }

  return sites;
};

export const getAllSites = async (allowedSiteCollections?: string[], pageData?: IGraphSearchPager, enableProvisionedSiteRegistration?: boolean, managedSitesDatasources?: docubirdManagedSitesDatasource[], clientListUrl?: string): Promise<ISiteSearchResult> => {

  const isNextPageRequest: boolean = pageData?.moreResultsAvailable;

  // 2026-10-27: the markers a first-page load overwrites below; put back when the load turns out to be an outage.
  const previousInactiveClientSites = SharePointSiteHelper.inactiveClientSites;
  const previousInactiveClientSitesLoaded = SharePointSiteHelper.inactiveClientSitesLoaded;
  const previousInactiveClientSitesComplete = SharePointSiteHelper.inactiveClientSitesComplete;
  // 2026-10-28: whether this load's own client-list read failed; only then are the markers put back (see below).
  let clientListReadFailed: boolean = false;
  // 2026-10-29 (audit A, final review): a sign-out's reset during the load ends the sign-in it reads for (see below).
  const resetsAtStart: number = SharePointSiteHelper.resets;

  let [allHubSites, allowedHubSites, managedSites, provisionedLoad, inactiveClientSites] = await Promise.all([
    !isNextPageRequest && getAllHubSites(),
    // Only the main search below merges the provisioned sites (2026-10-28): ids a failed load left behind would make
    // this search take a site not yet indexed in as a hub, and the main search would then have its registration removed.
    !isNextPageRequest && searchSharePointSites({ hubSitesOnly: true, allowedSites: allowedSiteCollections, skipProvisionedSites: true }),
    !isNextPageRequest && getDocubirdManagedSites(managedSitesDatasources),
    enableProvisionedSiteRegistration && !isNextPageRequest && getProvisionedSites(),
    !isNextPageRequest && clientListUrl?.trim()?.length > 0 && getInactiveClientSites(clientListUrl, { onComplete: complete => { if (SharePointSiteHelper.resets === resetsAtStart) SharePointSiteHelper.inactiveClientSitesComplete = complete; clientListReadFailed = !complete; } })
  ]);

  // 2026-10-29 (final review): a sign-out's reset during these reads ended the sign-in they were for. Their answers must not
  // reach the helper, which now belongs to the next sign-in: its markers, marked complete, would come back on that sign-in's
  // next failed read, and Copilot would trust them. getSites discards this load anyway.
  if (SharePointSiteHelper.resets !== resetsAtStart) {
    return { sites: [], pageData, outageFailure: true };
  }

  if (allHubSites?.length > 0 && allowedHubSites?.sites?.length > 0) {
    SharePointSiteHelper.clearHubs();

    allowedHubSites.sites.forEach(site => {
      site.isHub = true;

      const hub: IHubSite = allHubSites.find(hubSite => hubSite.siteUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase());
      if (hub) {
        site.hubId = hub.id;
        if (hub.title) site.name = hub.title;
        if (hub.description) site.description = hub.description;
        if (hub.parentHubSiteId && hub.parentHubSiteId !== "00000000-0000-0000-0000-000000000000") {
          const parentHub: IHubSite = allHubSites.find(hubSite => hubSite.id === hub.parentHubSiteId);
          if (parentHub && allowedHubSites.sites.findIndex(allowedHub => parentHub.siteUrl?.trim().toLowerCase() === allowedHub.webUrl?.trim().toLowerCase()) !== -1) {
            site.parentHubId = hub.parentHubSiteId;
          }
        }
      }
      else {
        const siteIdParts: string[] = site.id?.split(",") // xblox.sharepoint.com,058798cc-59c1-451f-8957-49e32c49aeb1,4c5fa046-b695-4b4b-86ef-b47bb86f7cbe
        if (Array.isArray(siteIdParts) && siteIdParts.length > 1 && siteIdParts[1]) {
          site.hubId = siteIdParts[1];
        }
      }

      if (site.hubId) {
        SharePointSiteHelper.hubSites.push(site);
      }
    });

  }

  if (Array.isArray(managedSites?.sites) && managedSites.sites.length > 0) {
    SharePointSiteHelper.managedSites = managedSites;
  }

  if (Array.isArray(provisionedLoad?.sites) && provisionedLoad.sites.length > 0) {
    SharePointSiteHelper.provisionedSites = provisionedLoad.sites;
  }

  // Refresh the global inactive-client set on every full (first-page) load so it clears when the
  // client list is emptied/disabled or a fetch fails; leave it untouched during pagination.
  let assignedInactiveClientSites: IManagedSite[];
  if (!isNextPageRequest) {
    assignedInactiveClientSites = Array.isArray(inactiveClientSites) ? inactiveClientSites as IManagedSite[] : [];
    SharePointSiteHelper.inactiveClientSites = assignedInactiveClientSites;
    SharePointSiteHelper.inactiveClientSitesLoaded = true;
  }

  const searchRequest: ISiteSearchRequest = {
    from: pageData?.from,
    allowedSites: allowedSiteCollections,
    hubSiteIds: SharePointSiteHelper.hubSites.map(hub => hub.hubId),
    pageLimit: Array.isArray(managedSites?.sites) && managedSites.sites.length > 100 ? 5 : 1
  };

  let siteCollections: ISiteSearchResult;
  let hubSiteChildren: ISiteSearchResult;
  let subSites: ISiteListLoad;
  [siteCollections, hubSiteChildren, subSites] = await Promise.all([
    searchSharePointSites(searchRequest),
    !isNextPageRequest && SharePointSiteHelper.hubSites.length > 0 && searchSharePointSites({ ...searchRequest, hubChildrenOnly: true, pageLimit: 5, skipProvisionedSites: true }),
    !isNextPageRequest && getAllSubSites(searchRequest)
  ]);

  if (!isNextPageRequest) {
    if (SharePointSiteHelper.hubSites.length > 0) {
      // 2026-10-29: a provisioned site the hub-children search found is indexed: its registration goes, and so does the copy
      // the main search fetched for it (search leaves hub children out of that one), so it is listed under its hub only.
      // The hub-children search doesn't merge the provisioned sites itself (review N1), so nothing else would remove it.
      const indexedHubChildren: string[] = (provisionedLoad?.sites ?? [])
        .filter(registration => hubSiteChildren?.sites?.some(site => site.isHubChild && isRegisteredSite(site, registration)));
      if (indexedHubChildren.length > 0) {
        siteCollections.sites = (siteCollections?.sites ?? []).filter(site => !indexedHubChildren.some(registration => isRegisteredSite(site, registration)));
        // For the account the answer was asked for, while it is still signed in. Not awaited, as in the main search; rejects
        // when the Docubird API is down (already logged): the next load asks again.
        if (sameAccountId(provisionedLoad.accountId, (await authModule.getAccount(false))?.accountId)) {
          apiRemoveProvisionedSites(provisionedLoad.accountId, indexedHubChildren).catch(() => undefined);
        }
      }

      siteCollections.sites = SharePointSiteHelper.hubSites
        .concat(
          (siteCollections?.sites ?? []).concat(hubSiteChildren?.sites ?? [])
            .filter(site => !allHubSites.some(hubSite => hubSite.siteUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase()))
        );
      // .filter(site => !site.parentHubId);
    }

    if (Array.isArray(subSites?.sites) && subSites.sites.length > 0) {
      siteCollections.sites = siteCollections.sites
        .concat(subSites.sites)
        .filter(filterNonTopLevelISiteInfoArray);
    }
  }
  else if (SharePointSiteHelper.hubSites.length > 0) {
    siteCollections.sites = siteCollections.sites
      .filter(site => !SharePointSiteHelper.hubSites.some(hubSite => hubSite.webUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase()));
  }

  // A source that did not run (false) is not counted; hub metadata, managed/provisioned sites and the client list never are.
  // Neither is a hub search whose sites were not used (no hub metadata, or no hubs): it adds nothing to the list (2026-10-28).
  const hubSitesUsed: boolean = allHubSites?.length > 0 && allowedHubSites?.sites?.length > 0;
  const sourcesRun: ISiteListLoad[] = (isNextPageRequest ? [siteCollections] : [allowedHubSites, siteCollections, hubSiteChildren, subSites])
    .filter((source): source is ISiteListLoad => !!source);
  const listSources: ISiteListLoad[] = sourcesRun.filter(source => source !== allowedHubSites || hubSitesUsed);
  // 2026-10-28: when the Docubird API did not answer for the provisioned sites, the load is written only when every
  // list source that ran answered and the API's last answer is known. Before, the failed call ended the load before
  // anything was written, which kept the list and its cache; a partial list must not replace them now.
  const provisionedUnanswered: boolean = !!provisionedLoad?.failed && (!provisionedLoad.known || sourcesRun.some(source => source.outageFailure === true));
  const outageFailure: boolean = (listSources.length > 0 && listSources.every(source => source.outageFailure === true)) || provisionedUnanswered;

  // The main search carries its own flag; the combined answer is an outage only when every source was.
  // On an outage the result is discarded, so the sites are not marked: hub site objects can be the very objects of the kept list.
  if (!outageFailure) {
    siteCollections.sites = dedupeSitesByIdentity(siteCollections?.sites);
    SharePointSiteHelper.configureManagesSites(siteCollections?.sites);
    SharePointSiteHelper.configureInactiveSites(siteCollections?.sites);
    delete siteCollections.outageFailure;
  }
  else {
    siteCollections.outageFailure = true;
    // 2026-10-28: put back only what this load's own failed read overwrote, and only while nothing else has written the
    // markers since (an overlapping load, Copilot's own read, a sign-out's reset): those are newer than the snapshot, and a
    // read that succeeded is fresh.
    // A sign-out's reset can't be undone here: one during the first reads stopped this load above, and a later one
    // replaced the markers this load wrote.
    if (!isNextPageRequest && clientListReadFailed && SharePointSiteHelper.inactiveClientSites === assignedInactiveClientSites && !SharePointSiteHelper.inactiveClientSitesComplete) {
      SharePointSiteHelper.inactiveClientSites = previousInactiveClientSites;
      SharePointSiteHelper.inactiveClientSitesLoaded = previousInactiveClientSitesLoaded;
      SharePointSiteHelper.inactiveClientSitesComplete = previousInactiveClientSitesComplete;
    }
  }

  return siteCollections;
};

const getAllHubSites = async (): Promise<IHubSite[]> => {
  let hubSites: IHubSite[];
  try {
    const sharePointAccessToken: string = await authModule.getSharePointAccessToken();
    hubSites = await getHubSites(sharePointAccessToken, await getBaseSiteUrl());
  } catch (error) {
    logger(error, "sharePointInfo.ts (getAllHubSites)");
  }
  return hubSites?.length > 0 ? hubSites : [];
};

const getBaseSiteUrl = async (): Promise<string> => {
  return (await authModule.getAccount(false))?.sharePointBaseUrl;
};

interface IProvisionedSitesLoad {
  sites: string[];
  // the Docubird API did not answer; sites is then its last answer, when that is known
  failed?: boolean;
  known?: boolean;
  // the account the answer was asked for (2026-10-29)
  accountId?: string;
}

// 2026-10-28: the sites provisioned since the last search index run. The call rejects when the Docubird API is down
// (already logged), which took the whole load down with it. Every answer is remembered (localStorage is per account)
// and the last one stands in while the API is down: searchSharePointSites clears SharePointSiteHelper.provisionedSites
// after each merge, so without it a site that search has not indexed yet would drop out of the list.
const getProvisionedSites = async (): Promise<IProvisionedSitesLoad> => {
  let accountId: string;
  try {
    accountId = (await authModule.getAccount())?.accountId;
    const provisionedSites: string[] = await apiGetProvisionedSites(accountId);
    // 2026-10-29 (audit C): remembered only under the account it was asked for: the saved copies follow the account signed in now
    if (sameAccountId(accountId, (await authModule.getAccount(false))?.accountId)) {
      setLocalStorage(localStorageKeys.provisionedSites, provisionedSites);
    }
    return { sites: provisionedSites, accountId };
  } catch (error) {
    const lastProvisionedSites = getLocalStorage(localStorageKeys.provisionedSites);
    return Array.isArray(lastProvisionedSites)
      ? { sites: lastProvisionedSites, failed: true, known: true, accountId }
      : { sites: [], failed: true, known: false, accountId };
  }
};

// The provisioned-sites answer remembered for the account signed in, when registration is on (for filterAllowedSites).
const rememberedProvisionedSites = (enableProvisionedSiteRegistration: boolean): string[] => {
  const remembered = enableProvisionedSiteRegistration ? getLocalStorage(localStorageKeys.provisionedSites) : [];
  return Array.isArray(remembered) ? remembered : [];
};

// Whether a provisioned-site registration names this site, as the search matches them (GraphService searchSharePointSites):
// by the site id or a part of it, in any case (2026-10-29).
const isRegisteredSite = (site: ISiteInfo, registration: string): boolean =>
  !!registration && site?.id?.toLowerCase().indexOf(registration.toLowerCase()) > -1;

// 2026-10-29: the sites of a kept or saved list that an answered load would list under the allowed-sites setting (all of them
// when it is empty). Search lets a site through by its name (searchSharePointSites, getAllSubSites). A hub carries its hub
// title instead, and a provisioned site is listed whatever the setting until search has indexed it, so both stay. A
// managed-site group's entry (SharePointSiteHelper.configureManagesSites) stays while one of its sites does: a grouped site
// is listed only under it (audit A).
const filterAllowedSites = (sites: ISiteInfo[], allowedSites: string[], provisionedSites: string[]): ISiteInfo[] => {
  if (!(allowedSites?.length > 0)) {
    return sites;
  }
  const sitesKept: ISiteInfo[] = sites.filter(site => !site.isManagedGroup && (site.isHub
    || allowedSites.some(allowedSite => site.name?.trim().toLowerCase() === allowedSite?.trim())
    || provisionedSites.some(registration => isRegisteredSite(site, registration))));
  return sites.filter(site => site.isManagedGroup
    ? sitesKept.some(siteKept => siteKept.managedGroup === site.id)
    : sitesKept.includes(site));
};

const getDocubirdManagedSites = async (managedSitesDatasources: docubirdManagedSitesDatasource[]): Promise<IManagedSiteCollection> => {
  const managedSiteCollection: IManagedSiteCollection = {
    groups: new Set<string>(),
    sites: []
  }

  if (Array.isArray(managedSitesDatasources) && managedSitesDatasources.length > 0) {
    await Promise.all(managedSitesDatasources
      .filter(ds => ds.siteUrl && ds.listName)
      .map(async ds => {
        try {
          const managedSites: IManagedSiteCollection = await getSharepointListItems(ds.siteUrl, ds.listName, ds.groupField);
          if (Array.isArray(managedSites?.sites)) {
            for (const managedSite of managedSites.sites) {
              managedSiteCollection.sites.push(managedSite);
            }
            if (managedSites?.groups?.size > 0) {
              managedSites.groups.forEach(group => {
                managedSiteCollection.groups.add(group);
              });
            }
          }
        } catch (error) {
          logger(error, "SharePointInfo.ts (getDocubirdManagedSites)");
        }
      }));
  }

  return managedSiteCollection;
}

type SharepointInfoAction = ReturnType<typeof setSharepointInfo | typeof setSitesLastCacheRefresh | typeof resetSharepointInfo | typeof setIsSitesLoading>;

export function sharePointInfoReducer(state = initialState, action: SharepointInfoAction): SharepointInfoState {
  switch (action.type) {
    case SET_SHAREPOINT_INFO:
      return action.payload;
    case SET_SITES_LAST_CACHE_REFRESH:
      return { ...state, lastCacheFetch: new Date().getTime() };
    case SET_IS_SITES_LOADING:
      return { ...state, isSitesLoading: action.payload };
    case RESET_SHAREPOINT_INFO:
      return initialState;
    default:
      return state;
  }
}
